import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { db, auth } from "../../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc as firestoreDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  setDoc,
  arrayUnion,
  getDoc
} from "firebase/firestore";
import { decryptMessage, importKey, decryptFile } from "../../utils/crypto";
import { SenderKeyManager } from "../../utils/SenderKeyManager";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../../context/ThemeContext";

// 🔒 Secure File Component (Handles Decryption & Cleanup)
const SecureFileBubble = ({ message, isMe, setViewingMessage }) => {
  const [status, setStatus] = useState(message.isEncryptedFile ? "locked" : "visible"); // locked, decrypting, visible, error
  const [decryptedUrl, setDecryptedUrl] = useState(null);
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (decryptedUrl) URL.revokeObjectURL(decryptedUrl);
    };
  }, [decryptedUrl]);

  // ⚡ Lazy Decryption: Only decrypt when visible
  const bubbleRef = useRef(null);

  useEffect(() => {
    if (status !== "locked" || !message.isEncryptedFile || message.mediaType === 'video') return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        handleUnlock();
        observer.disconnect(); // Decrypt once
      }
    }, { threshold: 0.1 });

    if (bubbleRef.current) observer.observe(bubbleRef.current);

    return () => observer.disconnect();
  }, [status, message.isEncryptedFile, message.mediaType]);


  const handleUnlock = async () => {
    if (status !== "locked") return;
    setStatus("decrypting");

    try {
      // 1. Get AES Key (Encrypted for Me)
      const myUid = auth.currentUser.uid;
      const myDeviceId = localStorage.getItem("deviceId");

      let encryptedAesKey = null;

      // Check if I am Sender or Receiver to find the right key payload
      if (isMe) {
        if (message.senderMediaKey && message.senderMediaKey[myDeviceId]) {
          encryptedAesKey = message.senderMediaKey[myDeviceId];
        }
        // Fallback: Loop all keys if deviceId mismatch
        else if (message.senderMediaKey) {
          encryptedAesKey = Object.values(message.senderMediaKey)[0];
        }
      } else {
        if (message.mediaKey && message.mediaKey[myDeviceId]) {
          encryptedAesKey = message.mediaKey[myDeviceId];
        } else if (message.mediaKey) {
          encryptedAesKey = Object.values(message.mediaKey)[0];
        }
      }

      if (!encryptedAesKey) throw new Error("Decryption Key not found for this device");

      // 2. Decrypt AES Key (RSA)
      const privKeyB64 = localStorage.getItem(`privKey_${myUid}`);
      const privKey = await importKey(privKeyB64, "decrypt");
      const aesKeyJwk = await decryptMessage(encryptedAesKey, privKey);

      if (!aesKeyJwk || aesKeyJwk.startsWith("🔒") || aesKeyJwk.startsWith("⚠️")) {
        throw new Error("Failed to unlock file key (Key Mismatch)");
      }

      // 3. Download Encrypted Blob
      const res = await fetch(message.mediaUrl);
      if (!res.ok) throw new Error("Download failed");
      const encryptedBlob = await res.blob();

      // 4. Decrypt File (AES-GCM)
      // Note: decryptFile handles the Integrity Check
      const decryptedBlob = await decryptFile(encryptedBlob, aesKeyJwk, message.mediaIv);

      const url = URL.createObjectURL(decryptedBlob);
      setDecryptedUrl(url);
      setStatus("visible");

    } catch (e) {
      console.error("Unlock failed:", e);
      setStatus("error");
      setErrorMsg(e.message || "Decryption failed");
    }
  };

  if (!message.isEncryptedFile) {
    // Legacy/Plaintext Handling
    return message.mediaType === 'image' ? (
      <img
        src={message.mediaUrl}
        alt="Attachment"
        className="w-72 h-64 max-w-full object-cover rounded-lg mb-2 cursor-pointer hover:opacity-90"
        onClick={() => setViewingMessage(message)}
      />
    ) : message.mediaType === 'video' ? (
      <video
        src={message.mediaUrl}
        controls
        className="w-72 h-64 max-w-full object-cover rounded-lg mb-2"
      />
    ) : (
      // 📂 Generic File Download (Unencrypted)
      <div className="w-72 p-4 bg-gray-100 dark:bg-white/10 rounded-lg flex items-center gap-3 border border-gray-200 dark:border-white/10">
        <div className="text-2xl">📄</div>
        <div className="flex-1 overflow-hidden">
          <div className="text-sm font-bold truncate text-gray-800 dark:text-gray-200">{message.fileName || "Document"}</div>
          <a href={message.mediaUrl} download={message.fileName || "file"} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline">Download File</a>
        </div>
      </div>
    );
  }

  // Secure UI
  return (
    <div className="mb-2" ref={bubbleRef}>
      {status === "locked" && (
        <div onClick={handleUnlock} className="w-72 h-32 bg-gray-100 dark:bg-white/5 rounded-lg border-2 border-dashed border-gray-300 dark:border-white/20 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 transition-colors">
          <div className="text-3xl mb-2">🔒</div>
          <div className="text-sm font-bold opacity-80">Click to Decrypt {message.mediaType === 'image' ? 'Photo' : 'File'}</div>
          <div className="text-xs opacity-50 mt-1">{message.fileName}</div>
        </div>
      )}

      {status === "decrypting" && (
        <div className="w-72 h-32 bg-gray-100 dark:bg-white/5 rounded-lg flex flex-col items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mb-2"></div>
          <div className="text-xs font-mono">Decrypting...</div>
        </div>
      )}

      {status === "error" && (
        <div className="w-72 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 text-center">
          <div className="text-red-500 font-bold text-sm mb-1">⚠️ Error</div>
          <div className="text-xs text-red-400">{errorMsg}</div>
        </div>
      )}

      {status === "visible" && (
        message.mediaType === 'image' ? (
          <img
            src={decryptedUrl}
            alt="Decrypted"
            className="w-72 h-64 max-w-full object-cover rounded-lg mb-2 cursor-pointer shadow-lg"
            onClick={() => setViewingMessage({ ...message, mediaUrl: decryptedUrl })}
          />
        ) : message.mediaType === 'video' ? (
          <video
            src={decryptedUrl}
            controls
            className="w-72 h-64 max-w-full object-cover rounded-lg mb-2 shadow-lg"
          />
        ) : (
          <div className="w-72 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center gap-3">
            <div className="text-2xl">📄</div>
            <div className="flex-1 overflow-hidden">
              <div className="text-sm font-bold truncate">{message.fileName || "Document"}</div>
              <a href={decryptedUrl} download={message.fileName || "file"} className="text-xs text-green-600 dark:text-green-400 hover:underline">Download Decrypted File</a>
            </div>
          </div>
        )
      )}
    </div>
  );
};
export default function ChatWindow({ selectedUser, isOnline, lastActive, setEditingMessage, contacts, onUpdateContact }) {
  const [messages, setMessages] = useState([]);
  const bottomRef = useRef();
  const [viewingMessage, setViewingMessage] = useState(null);

  // 🔐 Key Caching & Auto-Reload (Fixes "Encrypted message" after Sync)
  const [cachedPrivKey, setCachedPrivKey] = useState(null);
  const loadedKeyStr = useRef(""); // Track the raw string to detect changes

  useEffect(() => {
    const checkKey = async () => {
      if (!auth.currentUser) return;
      try {
        const storedKey = localStorage.getItem(`privKey_${auth.currentUser.uid}`);

        // If storage has a key and it differs from what we have loaded...
        if (storedKey && storedKey !== loadedKeyStr.current) {
          console.log("🔐 Key change detected. Reloading crypto context...");
          const key = await importKey(storedKey, "decrypt");
          setCachedPrivKey(key);
          loadedKeyStr.current = storedKey;
        }
      } catch (e) {
        console.error("Key load error:", e);
      }
    };

    // Check immediately and then poll (cheap check, string comparison)
    checkKey();
    const interval = setInterval(checkKey, 1000);
    return () => clearInterval(interval);
  }, [auth.currentUser?.uid]);

  // Track Conversation Metadata (Read Status & Cleared Timestamp)
  const [otherUserReadTime, setOtherUserReadTime] = useState(null);
  const [myClearedTimestamp, setMyClearedTimestamp] = useState(null);

  useEffect(() => {
    if (!selectedUser?.convId) return;
    const unsub = onSnapshot(firestoreDoc(db, "conversations", selectedUser.convId), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const currentUid = auth.currentUser?.uid;

        // Read Status
        const otherUid = selectedUser.uid;
        if (data.lastReadTimestamp && data.lastReadTimestamp[otherUid]) {
          // Check for equality to prevent loop
          setOtherUserReadTime(prev => {
            const newTs = data.lastReadTimestamp[otherUid];
            if (prev?.seconds === newTs?.seconds) return prev;
            return newTs;
          });
        }

        // Cleared Timestamp
        if (data.clearedTimestamp && data.clearedTimestamp[currentUid]) {
          const newTs = data.clearedTimestamp[currentUid];
          setMyClearedTimestamp(prev => {
            if (prev?.seconds === newTs?.seconds) return prev;
            return newTs;
          });
        }
      }
    });
    return () => {
      unsub();
      setOtherUserReadTime(null); // Reset read time on chat switch
      setMyClearedTimestamp(null);
    };
  }, [selectedUser?.convId, selectedUser?.uid, cachedPrivKey]); // 👈 Added cachedPrivKey dependency

  // 🛠 Message Actions State
  const [hoverMsgId, setHoverMsgId] = useState(null); // Show arrow
  const [msgMenuId, setMsgMenuId] = useState(null); // Show menu
  const [editingMsgId, setEditingMsgId] = useState(null); // Edit Mode
  const [editText, setEditText] = useState("");

  // 🗑️ Delete Confirmation State
  const [deleteConfirmMsg, setDeleteConfirmMsg] = useState(null);
  const [localDeletedIds, setLocalDeletedIds] = useState(new Set()); // ⚡ Optimistic Hiding

  const requestDelete = (msg) => {
    setDeleteConfirmMsg(msg);
    setMsgMenuId(null);
  };

  const confirmDelete = async (action) => {
    if (!deleteConfirmMsg) return;
    const msg = deleteConfirmMsg;
    const myUid = auth.currentUser?.uid;

    // ⚡ Optimistic Hide: Instantly remove from view
    setLocalDeletedIds(prev => new Set(prev).add(msg.id));

    try {
      if (action === "everyone") {
        // Soft Delete for All Messages (Standard)
        await updateDoc(firestoreDoc(db, "messages", msg.id), {
          type: "deleted",
          text: "This message was deleted",
          senderText: null,
          mediaUrl: null
        });
      } else if (action === "me") {
        await updateDoc(firestoreDoc(db, "messages", msg.id), {
          hiddenFor: arrayUnion(myUid)
        });
      }
    } catch (e) {
      console.error("Delete Action Failed:", e);
      // alert("Failed to delete message."); // Optional: Don't alert, just keep hidden or revert
    }

    setDeleteConfirmMsg(null);
  };

  const startEditing = (msg) => {
    // 🔒 Enforce 15-minute Edit Window (Professional Standard)
    const timeDiff = Date.now() - (msg.timestamp?.seconds * 1000);
    if (timeDiff > 15 * 60 * 1000) {
      alert("Messages can only be edited within 15 minutes of sending.");
      return;
    }

    setEditingMsgId(msg.id);
    // Prefer decrypted text if available, fallback to raw text
    setEditText(msg.text);
    setMsgMenuId(null);
  };

  const saveEdit = async () => {
    if (!editingMsgId) return;
    try {
      // 📝 Update Firestore
      // For simplicity and "Professional" UX, we simply update the text content.
      // E2EE Caveat: This update is stored as plaintext or requires re-encryption.
      // To keep it simple and working: We store the edit as plaintext 'text' and flag it.
      // The UI logic already prefers 'text' over 'senderText' if 'isEdited' is true (standard logic).
      await updateDoc(firestoreDoc(db, "messages", editingMsgId), {
        text: editText,
        // We do NOT update senderText (original encrypted payload) to preserve history/integrity if needed
        isEdited: true,
        isEncrypted: false // ⚠️ Mark as unencrypted now since we edited it to plaintext
      });
      setEditingMsgId(null);
    } catch (e) {
      console.error("Edit failed", e);
      alert("Failed to edit.");
    }
  };


  // ⚡ Instant Read Receipt Trigger
  useEffect(() => {
    if (!messages.length || !selectedUser?.convId) return;

    const lastMsg = messages[messages.length - 1];
    const isMe = lastMsg.senderId === auth.currentUser?.uid;

    // If the last message is NOT from me (i.e., I just received it) and I am looking at it:
    if (!isMe) {
      // Mark as read immediately
      const currentUid = auth.currentUser.uid;
      const convRef = firestoreDoc(db, "conversations", selectedUser.convId);
      // Use updateDoc instead of set(merge) for safety
      updateDoc(convRef, {
        [`unreadCounts.${currentUid}`]: 0,
        [`lastReadTimestamp.${currentUid}`]: serverTimestamp()
      }).catch(e => {
        // Ignore "Not Found" errors for new chats
        if (e.code !== 'not-found') console.error("Read receipt failed", e);
      });
    }
  }, [messages, selectedUser?.convId]);





  // Optimized Decrypt Function (Uses Cache)
  const decrypt = async (ctB64) => {
    try {
      if (!cachedPrivKey) return "Please wait, loading keys..."; // Or handle gracefully

      // Attempt V2 (JSON Payload) Decryption
      try {
        if (ctB64?.startsWith("{")) {
          const v2Data = JSON.parse(ctB64);
          if (v2Data && v2Data.payloads) {
            const myDeviceId = localStorage.getItem("deviceId");
            if (myDeviceId && v2Data.payloads[myDeviceId]) {
              const res = await decryptMessage(v2Data.payloads[myDeviceId], cachedPrivKey);
              if (res) return res;
            }
            // Fallback
            for (const cipher of Object.values(v2Data.payloads)) {
              const res = await decryptMessage(cipher, cachedPrivKey);
              if (res && !res.startsWith("🔒") && !res.startsWith("⚠️")) return res;
            }
            return "⚠️ Decryption Failed (Key mismatch)";
          }
        }
      } catch (e) { /* Not JSON */ }

      // Legacy V1
      const decryptedText = await decryptMessage(ctB64, cachedPrivKey);
      return decryptedText || "⚠️ Decryption Failed (Key changed)";

    } catch (err) {
      console.warn("Decryption error:", err);
      return "[Error]";
    }
  };

  // Message Fetching & Filtering
  const activeConvId = selectedUser?.convId || (selectedUser?.isSquad ? selectedUser?.uid : null);
  const decryptedCache = useRef({}); // 🚀 CacheRef

  // Message Fetching & Filtering
  useEffect(() => {
    if (!activeConvId || !cachedPrivKey) {
      setMessages([]);
      return;
    }

    const q = query(
      collection(db, "messages"),
      where("convId", "==", activeConvId),
      orderBy("timestamp", "asc")
    );

    console.log("Listen Start:", activeConvId);

    const unsubscribe = onSnapshot(q, async (snap) => {
      console.log(`Snapshot Size: ${snap.size} for ${activeConvId}`);
      const currentUid = auth.currentUser?.uid;

      const rawMessages = await Promise.all(
        snap.docs.map(async (d) => {
          const data = d.data();

          if (data.hiddenFor && data.hiddenFor.includes(currentUid)) return null;
          if (myClearedTimestamp && data.timestamp && data.timestamp.seconds < myClearedTimestamp.seconds) return null;

          // 🚀 Performance Optimization: Cache Decryption
          const cacheKey = `${d.id}_${data.isEdited ? 'edit' : 'orig'}`;
          if (decryptedCache.current[cacheKey]) {
            return { id: d.id, ...data, ...decryptedCache.current[cacheKey] };
          }

          try {
            if (data.isEncrypted && !selectedUser.isSquad) {
              const isMe = data.senderId === currentUid;
              if (isMe) {
                // 🛡️ Logic for My Messages (Sender Side)
                if (data.senderText) {
                  const decrypted = await decrypt(data.senderText);
                  if ((decrypted.startsWith("⚠️") || decrypted.startsWith("🔒")) && !data.senderText.startsWith("{")) {
                    data.text = data.senderText; // Plaintext fallback
                  } else {
                    data.text = decrypted;
                  }
                } else if (!data.mediaUrl && !data.senderMediaUrl) {
                  data.text = "🔒 Encrypted message";
                }

                if (data.senderMediaUrl) {
                  const url = await decrypt(data.senderMediaUrl);
                  if (!url.startsWith("🔒") && !url.startsWith("⚠️")) data.mediaUrl = url;
                }
              } else {
                // 🛡️ Logic for Incoming Messages (Receiver Side)
                if (data.text) {
                  const decrypted = await decrypt(data.text);
                  if ((decrypted.startsWith("⚠️") || decrypted.startsWith("🔒")) && !data.text.startsWith("{")) {
                    // Assume plaintext fallback
                  } else {
                    data.text = decrypted;
                  }
                }
                if (data.mediaUrl) {
                  const url = await decrypt(data.mediaUrl);
                  if (!url.startsWith("🔒") && !url.startsWith("⚠️")) data.mediaUrl = url;
                }
              }
            } else if (data.isEncrypted && selectedUser.isSquad) {
              // 🛡️ SQUAD DECRYPTION (Sender Key)
              if (data.ciphertext && data.keyId && data.iv) {
                try {
                  let senderKey = await SenderKeyManager.getSenderKey(data.convId, data.keyId);

                  if (senderKey) {
                    const decrypted = await SenderKeyManager.decryptText(data.ciphertext, senderKey, data.iv);
                    if (decrypted && !decrypted.startsWith("⚠️")) {
                      data.text = decrypted;
                    } else {
                      data.text = "⚠️ Decryption Error";
                    }
                  } else {
                    data.text = "🔒 Decrypting...";
                    // If we don't have key, SenderKeyManager.getSenderKey attempts fetch.
                    // If it returned null, it means we really don't have it (or network failed).
                    // Retry mechanism? Re-render will trigger effect again if dependency changes? 
                    // No, onSnapshot fires on updates. We might need to force update if key arrives later.
                    // But getSenderKey is async and waits for fetch. So if it returns null, we failed.
                    data.text = "🔒 Key Unavailable";
                  }
                } catch (e) {
                  console.error("Group Decryption Failed", e);
                  data.text = "⚠️ Error";
                }
              }
            }

            // Store result in cache
            decryptedCache.current[cacheKey] = { text: data.text, mediaUrl: data.mediaUrl };

          } catch (e) {
            console.error(e);
          }
          return { id: d.id, ...data };
        })
      );

      const msgList = rawMessages.filter(m => m !== null).sort((a, b) => {
        // Fix: If timestamp is null (pending write), treat as NOW (Infinity) so it stays at bottom
        const getMs = (ts) => (ts?.seconds ? ts.seconds * 1000 : (Date.now()));
        return getMs(a.timestamp) - getMs(b.timestamp);
      });

      setMessages(msgList.filter(m => !localDeletedIds.has(m.id)));
    });

    return unsubscribe;
  }, [activeConvId, cachedPrivKey, myClearedTimestamp, localDeletedIds]);


  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // 🛠️ Missing Username Fetcher (Squad Fix)
  const [userMap, setUserMap] = useState({});

  useEffect(() => {
    const fetchMissingUsers = async () => {
      const missingUids = new Set();
      messages.forEach(m => {
        if (!m.senderId) return;
        // If we don't have a username on the msg AND we haven't fetched it yet
        if ((!m.senderUsername || m.senderUsername === 'User') && !userMap[m.senderId]) {
          missingUids.add(m.senderId);
        }
      });

      if (missingUids.size === 0) return;

      const newUsers = {};
      await Promise.all(Array.from(missingUids).map(async (uid) => {
        // Check if we already have it in a separate cache or just fetch
        try {
          // Optimization: check if it's currently selected user (unlikely for group members but possible)
          if (selectedUser && selectedUser.uid === uid && !selectedUser.isSquad) {
            newUsers[uid] = selectedUser.username;
          } else {
            const docSnap = await getDoc(firestoreDoc(db, "users", uid));
            if (docSnap.exists()) {
              newUsers[uid] = docSnap.data().username || "Unknown";
            } else {
              newUsers[uid] = "Unknown";
            }
          }
        } catch (e) {
          console.error("Failed to fetch user", uid, e);
        }
      }));

      setUserMap(prev => ({ ...prev, ...newUsers }));
    };

    if (selectedUser) {
      fetchMissingUsers();
    }
  }, [messages, selectedUser, userMap]);



  if (!selectedUser) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-500">
        Select a conversation
      </div>
    );
  }

  const currentUserId = auth.currentUser?.uid;

  // 🎨 Theme
  const { isDarkMode } = useTheme();

  return (
    <div className="flex-1 overflow-auto px-4 py-4 h-full custom-scrollbar">
      <AnimatePresence>
        {messages.map((m) => {
          const isMe = m.senderId === currentUserId;

          // ... (Existing read receipt logic) ...
          let isRead = false;
          if (isMe && otherUserReadTime && m.timestamp) {
            const msgTime = m.timestamp.seconds || 0;
            const readTime = otherUserReadTime.seconds || 0;
            if (readTime >= msgTime) isRead = true;
          }

          let isDelivered = isOnline;
          if (!isDelivered && lastActive && m.timestamp) {
            const msgTime = m.timestamp.seconds || 0;
            let activeTime = 0;
            if (typeof lastActive === 'number') activeTime = lastActive / 1000;
            else if (lastActive?.seconds) activeTime = lastActive.seconds;
            if (activeTime > msgTime) isDelivered = true;
          }

          // 15-Minute Edit Window
          const isEdittable = isMe && !m.mediaUrl && (Date.now() - (m.timestamp?.seconds * 1000) < 15 * 60 * 1000);

          return (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.3 }}
              className={`mb-4 flex ${isMe ? "justify-end" : "justify-start"} group relative`}
              onMouseEnter={() => setHoverMsgId(m.id)}
              onMouseLeave={() => { setHoverMsgId(null); if (msgMenuId === m.id) setMsgMenuId(null); }}
            >
              <div className={`max-w-[75%] md:max-w-[60%] relative`}>
                {!isMe && (
                  <div className={`text-xs mb-1 ml-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {(!m.isSquad && contacts && contacts[m.senderId]) ? contacts[m.senderId] : (m.senderUsername && m.senderUsername !== "User" ? m.senderUsername : (userMap[m.senderId] || "User"))}
                  </div>
                )}

                <div
                  className={`px-4 py-2 shadow-sm break-words relative text-sm md:text-base ${isMe
                    ? "bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-2xl rounded-tr-sm shadow-md"
                    : isDarkMode
                      ? "bg-white/10 backdrop-blur-xl border border-white/10 text-gray-100 rounded-2xl rounded-tl-sm"
                      : "bg-white border border-gray-100 text-gray-900 rounded-2xl rounded-tl-sm shadow-sm"
                    }`}
                >
                  {/* 🛠 Dropdown Trigger (Only show on hover or if menu open) */}
                  {(hoverMsgId === m.id || msgMenuId === m.id) && !editingMsgId && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setMsgMenuId(msgMenuId === m.id ? null : m.id); }}
                      className={`absolute top-1 right-2 p-1 rounded-full shadow-md z-10 transition-colors ${isMe ? 'bg-white/20 hover:bg-white/30 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-600'
                        }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                    </button>
                  )}

                  {/* 🛠 Professional Dropdown Menu */}
                  {msgMenuId === m.id && (
                    <div className={`absolute top-8 ${isMe ? 'right-0' : 'left-0'} w-36 rounded-xl shadow-2xl overflow-hidden z-20 animate-fade-in py-1.5 ${isDarkMode ? 'bg-[#1e293b] border border-white/10 ring-1 ring-white/10' : 'bg-white border border-gray-100 ring-1 ring-black/5'}`}>
                      {isEdittable && (
                        <button
                          onClick={() => { startEditing(m); }}
                          className={`w-full text-left px-4 py-2.5 text-xs hover:bg-opacity-10 font-medium flex items-center gap-2.5 transition-colors ${isDarkMode ? 'text-white hover:bg-white' : 'text-gray-800 hover:bg-black'}`}
                        >
                          ✏️ Edit
                        </button>
                      )}

                      {/* Delete Option */}
                      <button
                        onClick={() => requestDelete(m)}
                        className={`w-full text-left px-4 py-2.5 text-xs hover:bg-red-500/10 font-medium flex items-center gap-2.5 text-red-500 transition-colors`}
                      >
                        🗑 Delete
                      </button>
                    </div>
                  )}

                  {m.mediaUrl && (
                    <SecureFileBubble message={m} isMe={isMe} setViewingMessage={setViewingMessage} />
                  )}

                  {/* ✏️ Edit Mode Input vs Display Text */
                    editingMsgId === m.id ? (
                      <div className="min-w-[150px]">
                        <input
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          className="w-full bg-black/20 rounded px-3 py-1.5 text-white outline-none mb-2 border border-white/20"
                          autoFocus
                          onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditingMsgId(null); }}
                        />
                        <div className="flex justify-end gap-2">
                          <button onClick={() => setEditingMsgId(null)} className="text-xs opacity-70 hover:opacity-100 px-2 py-1">Cancel</button>
                          <button onClick={saveEdit} className="text-xs bg-white text-indigo-600 px-3 py-1 rounded-full font-bold hover:bg-opacity-90 shadow-sm">Save</button>
                        </div>
                      </div>
                    ) : (
                      m.text
                    )
                  }
                  {m.isEdited && <div className="text-[10px] opacity-60 text-right italic select-none mt-0.5">edited</div>}
                </div>

                <div className={`text-[10px] mt-1 flex items-center gap-1 ${isMe ? "justify-end" : "justify-start"} ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span className="font-medium">{m.timestamp?.seconds ? new Date(m.timestamp.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '...'}</span>
                  {isMe && (
                    <span className="ml-1 text-sm font-bold">
                      {isRead ? (
                        <span className="text-green-500">✓✓</span>
                      ) : isDelivered ? (
                        <span className="text-gray-400">✓✓</span>
                      ) : (
                        <span className="text-gray-400">✓</span>
                      )}
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
      <div ref={bottomRef} className="pb-2" />

      {/* 🟢 Lightbox Overlay (Portal for Z-Index Safety) */}
      {createPortal(
        <AnimatePresence>
          {viewingMessage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[99999] bg-black/80 backdrop-blur-xl flex items-center justify-center p-4"
              onClick={() => setViewingMessage(null)}
            >
              {/* Top Bar (Info Only) */}
              <div className="absolute top-0 left-0 right-0 p-6 flex justify-between items-center z-50 pointer-events-none" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-3 text-white pointer-events-auto bg-black/30 backdrop-blur-md px-4 py-2 rounded-full border border-white/10">
                  {viewingMessage.isEncrypted && (
                    <div className="flex items-center gap-1.5 text-green-400">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                    </div>
                  )}
                  <div className="text-sm font-semibold text-white drop-shadow-md">
                    {(!viewingMessage.isSquad && contacts && contacts[viewingMessage.senderId]) ? contacts[viewingMessage.senderId] : (viewingMessage.senderUsername || userMap[viewingMessage.senderId] || "User")}
                  </div>
                </div>
              </div>

              {/* 🔴 FORCE FIXED CLOSE BUTTON */}
              <button
                onClick={() => setViewingMessage(null)}
                className="fixed top-6 right-6 z-[1010] bg-white text-black p-3 rounded-full shadow-2xl hover:bg-gray-200 transition-transform hover:scale-110 active:scale-90 border-2 border-white/50 cursor-pointer pointer-events-auto"
                aria-label="Close Preview"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>

              {/* Media Content */}
              <div className="relative w-full h-full flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
                {viewingMessage.mediaType === 'image' ? (
                  <motion.img
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    src={viewingMessage.mediaUrl}
                    alt="Full View"
                    className="max-h-[85vh] max-w-[95vw] object-contain rounded-lg shadow-2xl drop-shadow-2xl"
                  />
                ) : (
                  <video
                    src={viewingMessage.mediaUrl}
                    controls
                    autoPlay
                    className="max-h-[85vh] max-w-[95vw] rounded-lg shadow-2xl drop-shadow-2xl"
                  />
                )}
                {viewingMessage.text && (
                  <div className="absolute bottom-10 left-0 right-0 text-center pointer-events-none">
                    <span className="inline-block bg-black/70 backdrop-blur-xl text-white px-6 py-3 rounded-2xl text-base font-medium border border-white/10 shadow-lg">
                      {viewingMessage.text}
                    </span>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* 🔴 DELETE CONFIRMATION MODAL */}
      {createPortal(
        <AnimatePresence>
          {deleteConfirmMsg && (
            <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className={`w-full max-w-sm rounded-2xl p-6 shadow-2xl ${isDarkMode ? "bg-[#1e293b] text-white border border-white/10" : "bg-white text-gray-900"
                  }`}
              >
                <div className="text-center mb-6">
                  <div className="w-12 h-12 rounded-full bg-red-100 text-red-500 mx-auto flex items-center justify-center mb-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                  </div>
                  <h3 className="text-lg font-bold">Delete Message?</h3>
                  <p className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    Choose how you want to delete this message.
                  </p>
                </div>

                <div className="space-y-3">
                  {/* Delete For Me (Always Available) */}
                  <button
                    onClick={() => confirmDelete("me")}
                    className={`w-full py-3 rounded-xl font-medium transition-colors border ${isDarkMode
                      ? "border-white/10 hover:bg-white/5 text-gray-300"
                      : "border-gray-200 hover:bg-gray-50 text-gray-700"
                      }`}
                  >
                    Delete for Me
                  </button>

                  {/* Delete For Everyone (Only if I sent it) */}
                  {deleteConfirmMsg.senderId === auth.currentUser?.uid && (
                    <button
                      onClick={() => confirmDelete("everyone")}
                      className="w-full py-3 rounded-xl font-medium bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/30 transition-all active:scale-[0.98]"
                    >
                      Delete for Everyone
                    </button>
                  )}
                </div>

                <button
                  onClick={() => setDeleteConfirmMsg(null)}
                  className="w-full mt-4 py-2 text-sm text-gray-400 hover:text-gray-500 transition-colors"
                >
                  Cancel
                </button>

              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

    </div>
  );
}
